import { useHotkeys } from "react-hotkeys-hook";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "radix-ui";
import { Link, useHistory } from "react-router-dom";
import { clsx } from "clsx";

import { Input } from "../Input/Input";
import { useNavItems } from "@/NavBar/NavBar";
import { NavItemProps, RenderNavItemsProps } from "@/NavBar/types";
import { useWizardCommands } from "@/wizards/framework/useWizardCommands";

export function Commandbar() {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  useHotkeys("meta+k,ctrl+k", () => setOpen((o) => !o));

  const handleQueryInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setQuery(e.target.value);
      setSelectedIndex(0);
    },
    [],
  );

  const close = useCallback(() => {
    setOpen(false);
  }, []);
  const [query, setQuery] = useState("");
  const commands = useSearchableCommands();

  const filteredCommands = useMemo(
    () => filterCommands(commands, query),
    [commands, query],
  );

  const navigate = useHistory().push;
  const runCommand = useCallback(
    (command: Command) => {
      setOpen(false);
      if ("href" in command) {
        navigate(command.href);
      } else {
        command.run();
      }
    },
    [navigate],
  );

  const handleInputKeydown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(Math.max(selectedIndex - 1, 0));
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex(
          Math.min(selectedIndex + 1, filteredCommands.length - 1),
        );
      }
      if (e.key === "Enter") {
        const command = filteredCommands[selectedIndex];
        if (command) {
          runCommand(command);
        }
      }
    },
    [selectedIndex, filteredCommands, runCommand],
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const hasIcons = filteredCommands.some((command) => command.Icon);
  useEffect(() => {
    const selectedItem = wrapperRef.current?.querySelector(
      `[data-index="${selectedIndex}"]`,
    );
    if (selectedItem instanceof HTMLElement) {
      selectedItem.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedIndex]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Title className="sr-only">Command bar</Dialog.Title>
        <Dialog.Description className="sr-only">
          Search for pages and actions
        </Dialog.Description>
        <Dialog.Content
          onEscapeKeyDown={close}
          onPointerDownOutside={close}
          className="border-dividerBold data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut shadow-elevation-200 z-overlay bg-surface-sunken/90 fixed top-1/6 left-1/2 w-125 -translate-x-1/2 space-y-4 rounded-lg border p-4"
        >
          <Input
            value={query}
            onChange={handleQueryInputChange}
            label="Search commands"
            onKeyDown={handleInputKeydown}
          />

          <div className="max-h-96 overflow-auto rounded-lg" ref={wrapperRef}>
            {filteredCommands.map((command, i) => {
              const className = clsx(
                "hover:bg-surface-hover/90 flex w-full cursor-pointer items-center gap-4 rounded-lg px-4 py-2 text-left",
                i === selectedIndex && "bg-fill-neutral/70",
              );
              const content = (
                <>
                  {command.Icon ? (
                    <span aria-hidden className="flex shrink-0">
                      <command.Icon className="size-8" />
                    </span>
                  ) : (
                    hasIcons && <span className="size-8 shrink-0" aria-hidden />
                  )}
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="truncate" title={command.title}>
                      {command.title}
                    </span>
                    {command.label && (
                      <span className="text-textSubtle line-clamp-2 text-xs">
                        {command.label}
                      </span>
                    )}
                  </span>
                </>
              );
              return "href" in command ? (
                <Link
                  to={command.href}
                  key={command.title}
                  data-index={`${i}`}
                  className={className}
                >
                  {content}
                </Link>
              ) : (
                <button
                  type="button"
                  key={command.title}
                  data-index={`${i}`}
                  className={className}
                  onClick={() => runCommand(command)}
                >
                  {content}
                </button>
              );
            })}
            {!filteredCommands.length && (
              <div className="text-textSubtle flex items-center justify-center rounded-lg px-4 py-2">
                No commands found
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

type CommandBase = {
  title: string;
  label: string;
  searchKeys: string[];
  /** Decorative art before the title. */
  Icon?: React.ComponentType<{ className?: string }>;
};

type Command = CommandBase & ({ href: string } | { run: () => void });

export function filterCommands<C extends CommandBase>(
  commands: C[],
  query: string,
): C[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return commands.filter((command) =>
    terms.every((term) => command.searchKeys.some((key) => key.includes(term))),
  );
}

function useSearchableCommands() {
  const navItems = useNavItems();
  const wizardCommands = useWizardCommands();

  return useMemo<Command[]>(() => {
    const full = Object.values(navItems).flat();

    return [...wizardCommands, ...reduceNavItemsToCommands({ items: full })];
  }, [navItems, wizardCommands]);
}
function navItemToCommand(navItem: NavItemProps, prefix: string): Command {
  return {
    title: [prefix, navItem.title].filter((i) => i).join(" > "),
    label: navItem.label,
    href: navItem.href!,
    searchKeys: navItem.title
      .toLowerCase()
      .split(" ")
      .concat(prefix.toLowerCase().split(" ")),
  };
}

function reduceNavItemsToCommands(
  navItem: RenderNavItemsProps,
  prefix: string = "",
): Command[] {
  const commands: Command[] = [];
  navItem.items?.forEach((item) => {
    if (item.items) {
      commands.push(
        ...reduceNavItemsToCommands(
          item,
          [prefix, item.title].filter((i) => i).join(" > "),
        ),
      );
    } else if (item.href && (!item.shouldShowFn || item.shouldShowFn())) {
      commands.push(navItemToCommand(item, prefix));
    }
  });
  return commands;
}
